import { useMutation, useQuery } from '@tanstack/react-query';
import { patientPortalApi } from './patientPortalClient';

export const PATIENT_PORTAL_KEY = ['patient-portal'] as const;

/**
 * Public: everything behind a `/p/patient/:portalToken` link.
 *
 * `retry: false` for the same reason `usePublicClinic` does it — a bad or
 * retired link is bad on every attempt, and retrying only delays the message
 * the patient needs to see.
 */
export function usePatientPortal(portalToken: string | undefined) {
  return useQuery({
    queryKey: [...PATIENT_PORTAL_KEY, portalToken],
    queryFn: () => patientPortalApi.get(portalToken as string),
    enabled: !!portalToken,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Fetch one report's presigned URL.
 *
 * A mutation rather than a query, mirroring `useLabReportUrl`: the URL expires
 * in ~120 seconds, so it must be minted when a person taps the button and never
 * cached into a rendered page where it can go stale in place while still
 * looking clickable.
 */
export function usePortalReportUrl(portalToken: string | undefined) {
  return useMutation({
    mutationFn: (documentId: string) =>
      patientPortalApi.reportUrl(portalToken as string, documentId),
  });
}
